/**
 * Gift card recipient form — works with #add-item-form + cart/add.js (scripts.js).
 * Requires Shopify property names per gift card recipient docs.
 */
(function () {
  'use strict';

  var MAX_MESSAGE = 200;
  var MAX_NAME = 255;
  var MAX_SCHEDULE_DAYS = 90;
  var MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  function root() {
    return document.querySelector('[data-gift-card-recipient]');
  }

  function isEnabled(wrap) {
    var cb = wrap && wrap.querySelector('[data-recipient-enable]');
    return cb && cb.checked;
  }

  function syncMessageCount(wrap) {
    if (!wrap) return;
    var message = wrap.querySelector('[data-recipient-message]');
    var counter = wrap.querySelector('[data-recipient-message-count]');
    if (!message || !counter) return;
    var max = parseInt(message.getAttribute('maxlength'), 10) || MAX_MESSAGE;
    var len = message.value.length;
    counter.textContent = len + ' / ' + max;
    counter.classList.toggle('gift-card-recipient__char-count--near', len >= max - 20 && len < max);
    counter.classList.toggle('gift-card-recipient__char-count--max', len >= max);
  }

  function scrollFieldIntoView(el) {
    if (!el || typeof el.scrollIntoView !== 'function') return;
    var reduceMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'center',
      inline: 'nearest',
    });
  }

  function fieldErrorEl(wrap, fieldKey) {
    return wrap ? wrap.querySelector('[data-recipient-field-error="' + fieldKey + '"]') : null;
  }

  function inputForFieldKey(wrap, fieldKey) {
    var selectors = {
      email: '[data-recipient-email]',
      name: '[data-recipient-name]',
      message: '[data-recipient-message]',
      'send-on': '[data-recipient-send-on]',
    };
    return wrap ? wrap.querySelector(selectors[fieldKey]) : null;
  }

  function clearFieldError(wrap, fieldKey) {
    if (!wrap) return;
    var slot = fieldErrorEl(wrap, fieldKey);
    if (!slot) return;
    slot.innerHTML = '';
    slot.setAttribute('hidden', '');
    slot.setAttribute('aria-hidden', 'true');
    var input = inputForFieldKey(wrap, fieldKey);
    if (input) input.removeAttribute('aria-invalid');
  }

  function clearFieldErrors(wrap) {
    if (!wrap) return;
    var slots = wrap.querySelectorAll('[data-recipient-field-error]');
    slots.forEach(function (slot) {
      slot.innerHTML = '';
      slot.setAttribute('hidden', '');
      slot.setAttribute('aria-hidden', 'true');
    });
    wrap.classList.remove('gift-card-recipient--error');
    wrap.querySelectorAll(
      '[data-recipient-email], [data-recipient-name], [data-recipient-message], [data-recipient-send-on]'
    ).forEach(function (input) {
      input.removeAttribute('aria-invalid');
      input.setCustomValidity('');
    });
  }

  function showFieldError(wrap, fieldKey, message, focusEl) {
    if (!wrap) return false;
    clearFieldErrors(wrap);
    var slot = fieldErrorEl(wrap, fieldKey);
    if (slot) {
      slot.innerHTML =
        '<i class="fas fa-exclamation-triangle" aria-hidden="true"></i>' +
        '<span class="addtocart-inline-warning__text"></span>';
      var textEl = slot.querySelector('.addtocart-inline-warning__text');
      if (textEl) textEl.textContent = message;
      slot.removeAttribute('hidden');
      slot.setAttribute('aria-hidden', 'false');
    }
    if (focusEl) {
      focusEl.setAttribute('aria-invalid', 'true');
      if (slot && slot.id) focusEl.setAttribute('aria-describedby', slot.id);
      scrollFieldIntoView(focusEl);
      if (typeof focusEl.focus === 'function') focusEl.focus();
    } else if (slot) {
      scrollFieldIntoView(slot);
    }
    wrap.classList.add('gift-card-recipient--error');
    return false;
  }

  function setFieldsEnabled(wrap, on) {
    if (!wrap) return;
    var inputs = wrap.querySelectorAll(
      '[data-recipient-flag], [data-recipient-offset], [data-recipient-email], [data-recipient-name], [data-recipient-message], [data-recipient-send-on], [data-recipient-send-on-part]'
    );
    inputs.forEach(function (el) {
      el.disabled = !on;
    });
    setRecipientPanelOpen(wrap, on);
    if (on) {
      var email = wrap.querySelector('[data-recipient-email]');
      if (email) email.required = true;
      var offset = wrap.querySelector('[data-recipient-offset]');
      if (offset) offset.value = String(new Date().getTimezoneOffset());
      applySendOnWindow(wrap.querySelector('[data-recipient-send-on]'));
      syncMessageCount(wrap);
      var fields = wrap.querySelector('[data-recipient-fields]');
      var scrollTarget = fields || email || wrap;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          var active = document.activeElement;
          if (fields && active && fields.contains(active)) return;
          scrollFieldIntoView(scrollTarget);
        });
      });
    } else {
      clearRecipientFields(wrap);
    }
    clearFieldErrors(wrap);
  }

  function clearRecipientFields(wrap) {
    if (!wrap) return;
    var email = wrap.querySelector('[data-recipient-email]');
    var name = wrap.querySelector('[data-recipient-name]');
    var message = wrap.querySelector('[data-recipient-message]');
    var sendOn = wrap.querySelector('[data-recipient-send-on]');
    var offset = wrap.querySelector('[data-recipient-offset]');
    if (email) {
      email.value = '';
      email.required = false;
      email.setCustomValidity('');
      email.removeAttribute('aria-invalid');
    }
    if (name) name.value = '';
    if (message) message.value = '';
    if (sendOn) sendOn.value = '';
    if (offset) offset.value = '';
    var sendParts = wrap.querySelectorAll('[data-recipient-send-on-part]');
    sendParts.forEach(function (part) {
      part.value = '';
    });
    var yearPart = wrap.querySelector('[data-recipient-send-on-part="year"]');
    if (yearPart) {
      if (typeof window.motDispatchChange === 'function') window.motDispatchChange(yearPart);
      else if (document.createEvent) {
        var yearEv = document.createEvent('HTMLEvents');
        yearEv.initEvent('change', true, false);
        yearPart.dispatchEvent(yearEv);
      }
    }
    syncMessageCount(wrap);
  }

  /** HTML5 email validation (type="email" + checkValidity), aligned with Shopify Dawn recipient form. */
  function validateRecipientEmail(emailInput) {
    if (!emailInput) {
      return { ok: false, message: 'Please enter a recipient email address.' };
    }
    emailInput.required = true;
    emailInput.value = emailInput.value.trim();
    emailInput.setCustomValidity('');
    if (!emailInput.value) {
      return { ok: false, message: 'Please enter a recipient email address.' };
    }
    if (!emailInput.checkValidity()) {
      var msg = emailInput.validationMessage || 'Please enter a valid recipient email address.';
      if (emailInput.validity.typeMismatch) {
        msg = 'Please enter a valid recipient email address.';
      } else if (emailInput.validity.valueMissing) {
        msg = 'Please enter a recipient email address.';
      }
      return { ok: false, message: msg };
    }
    return { ok: true };
  }

  function parseSendOn(value) {
    if (!value || !value.trim()) return null;
    var parts = value.trim().split('-');
    if (parts.length !== 3) return null;
    var y = parseInt(parts[0], 10);
    var m = parseInt(parts[1], 10) - 1;
    var d = parseInt(parts[2], 10);
    var dt = new Date(y, m, d);
    if (dt.getFullYear() !== y || dt.getMonth() !== m || dt.getDate() !== d) return null;
    return dt;
  }

  function startOfDay(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  /** Local calendar date for <input type="date"> (avoid UTC shift from toISOString). */
  function formatLocalDateYmd(d) {
    var y = d.getFullYear();
    var m = d.getMonth() + 1;
    var day = d.getDate();
    return y + '-' + (m < 10 ? '0' : '') + m + '-' + (day < 10 ? '0' : '') + day;
  }

  function isIos13OrBelowDateClient() {
    if (typeof window.motIsIos13OrBelowCartClient === 'function') {
      return window.motIsIos13OrBelowCartClient();
    }
    var ua =
      typeof navigator !== 'undefined' && navigator.userAgent ? navigator.userAgent : '';
    if (!/iP(hone|ad|od)/i.test(ua)) return false;
    var m = ua.match(/OS (\d+)[._]/);
    if (!m) return false;
    return parseInt(m[1], 10) <= 13;
  }

  function addSelectOption(sel, value, label) {
    var opt = document.createElement('option');
    opt.value = value;
    opt.appendChild(document.createTextNode(label));
    sel.appendChild(opt);
  }

  function sendOnWindow() {
    var minDate = startOfDay(new Date());
    var maxDate = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
    maxDate.setDate(maxDate.getDate() + MAX_SCHEDULE_DAYS);
    return { minDate: minDate, maxDate: maxDate };
  }

  /* iOS reads min/max when the field is enabled/focused; attributes set while disabled are ignored. */
  function applySendOnWindow(sendOn) {
    if (!sendOn || sendOn.getAttribute('data-recipient-send-on-legacy') === 'true') return;
    var w = sendOnWindow();
    var minStr = formatLocalDateYmd(w.minDate);
    var maxStr = formatLocalDateYmd(w.maxDate);
    sendOn.setAttribute('min', minStr);
    sendOn.setAttribute('max', maxStr);
    sendOn.min = minStr;
    sendOn.max = maxStr;
  }

  function clampSendOnToWindow(sendOn) {
    if (!sendOn || !sendOn.value) return;
    var chosen = parseSendOn(sendOn.value);
    if (!chosen) return;
    var w = sendOnWindow();
    var chosenDay = startOfDay(chosen);
    if (chosenDay < w.minDate) sendOn.value = formatLocalDateYmd(w.minDate);
    else if (chosenDay > w.maxDate) sendOn.value = formatLocalDateYmd(w.maxDate);
  }

  function yearsInWindow(minDate, maxDate) {
    var years = [];
    var y;
    for (y = minDate.getFullYear(); y <= maxDate.getFullYear(); y++) {
      years.push(y);
    }
    return years;
  }

  function monthsInWindow(year, minDate, maxDate) {
    var months = [];
    var yStart = year ? parseInt(year, 10) : minDate.getFullYear();
    var yEnd = year ? parseInt(year, 10) : maxDate.getFullYear();
    var seen = {};
    var y;
    for (y = yStart; y <= yEnd; y++) {
      var mStart = y === minDate.getFullYear() ? minDate.getMonth() + 1 : 1;
      var mEnd = y === maxDate.getFullYear() ? maxDate.getMonth() + 1 : 12;
      var m;
      for (m = mStart; m <= mEnd; m++) {
        if (!seen[m]) {
          seen[m] = true;
          months.push(m);
        }
      }
    }
    return months;
  }

  function inferYearForMonth(month, minDate, maxDate) {
    if (!month) return '';
    var want = parseInt(month, 10);
    var found = '';
    var y;
    for (y = minDate.getFullYear(); y <= maxDate.getFullYear(); y++) {
      var mStart = y === minDate.getFullYear() ? minDate.getMonth() + 1 : 1;
      var mEnd = y === maxDate.getFullYear() ? maxDate.getMonth() + 1 : 12;
      if (want >= mStart && want <= mEnd) {
        if (found) return '';
        found = String(y);
      }
    }
    return found;
  }

  function dayBounds(year, month, minDate, maxDate) {
    if (!year || !month) return null;
    var y = parseInt(year, 10);
    var m = parseInt(month, 10);
    var dim = new Date(y, m, 0).getDate();
    var start = 1;
    var end = dim;
    if (y === minDate.getFullYear() && m === minDate.getMonth() + 1) start = minDate.getDate();
    if (y === maxDate.getFullYear() && m === maxDate.getMonth() + 1) end = maxDate.getDate();
    if (start > end) return null;
    return { start: start, end: end };
  }

  function refillSelect(sel, values, labels, keep, placeholder) {
    var prev = keep !== undefined && keep !== null ? String(keep) : sel.value;
    sel.innerHTML = '';
    addSelectOption(sel, '', placeholder);
    var found = false;
    var i;
    for (i = 0; i < values.length; i++) {
      addSelectOption(sel, String(values[i]), labels ? labels[i] : String(values[i]));
      if (String(values[i]) === prev) found = true;
    }
    sel.value = found ? prev : '';
  }

  function syncLegacySendOnValue(wrap) {
    var sendOn = wrap.querySelector('[data-recipient-send-on]');
    var monthSel = wrap.querySelector('[data-recipient-send-on-part="month"]');
    var daySel = wrap.querySelector('[data-recipient-send-on-part="day"]');
    var yearSel = wrap.querySelector('[data-recipient-send-on-part="year"]');
    if (!sendOn || !monthSel || !daySel || !yearSel) return;
    if (!monthSel.value || !daySel.value || !yearSel.value) {
      sendOn.value = '';
      return;
    }
    var mm = parseInt(monthSel.value, 10);
    var dd = parseInt(daySel.value, 10);
    sendOn.value =
      yearSel.value +
      '-' +
      (mm < 10 ? '0' : '') +
      mm +
      '-' +
      (dd < 10 ? '0' : '') +
      dd;
  }

  function legacySendOnFirstEmpty(wrap) {
    var parts = wrap.querySelectorAll('[data-recipient-send-on-part]');
    var i;
    for (i = 0; i < parts.length; i++) {
      if (!parts[i].value) return parts[i];
    }
    return wrap.querySelector('[data-recipient-send-on]');
  }

  function legacySendOnPartial(wrap) {
    var parts = wrap.querySelectorAll('[data-recipient-send-on-part]');
    if (!parts.length) return false;
    var filled = 0;
    var i;
    for (i = 0; i < parts.length; i++) {
      if (parts[i].value) filled++;
    }
    return filled > 0 && filled < parts.length;
  }

  /* iOS 13: appearance:none + type=date inside a previously hidden panel does not open wheels. */
  function bindLegacySendOn(wrap, sendOn) {
    if (!wrap || !sendOn || !isIos13OrBelowDateClient()) return;
    if (sendOn.getAttribute('data-recipient-send-on-legacy') === 'true') return;
    sendOn.setAttribute('data-recipient-send-on-legacy', 'true');
    sendOn.setAttribute('type', 'hidden');

    var windowDates = sendOnWindow();
    var minDate = windowDates.minDate;
    var maxDate = windowDates.maxDate;

    var row = document.createElement('div');
    row.className = 'gift-card-recipient__send-on-legacy';
    row.setAttribute('data-recipient-send-on-legacy-wrap', '');

    var monthSel = document.createElement('select');
    monthSel.className = 'gift-card-recipient__send-on-legacy-select';
    monthSel.setAttribute('data-recipient-send-on-part', 'month');
    monthSel.setAttribute('aria-label', 'Send on month');
    monthSel.disabled = !!sendOn.disabled;
    if (sendOn.id) monthSel.id = sendOn.id + '-month';

    var daySel = document.createElement('select');
    daySel.className = 'gift-card-recipient__send-on-legacy-select';
    daySel.setAttribute('data-recipient-send-on-part', 'day');
    daySel.setAttribute('aria-label', 'Send on day');
    daySel.disabled = !!sendOn.disabled;

    var yearSel = document.createElement('select');
    yearSel.className = 'gift-card-recipient__send-on-legacy-select';
    yearSel.setAttribute('data-recipient-send-on-part', 'year');
    yearSel.setAttribute('aria-label', 'Send on year');
    yearSel.disabled = !!sendOn.disabled;

    function refreshParts(source) {
      var years = yearsInWindow(minDate, maxDate);
      if (source === 'month' && monthSel.value && !yearSel.value) {
        var inferred = inferYearForMonth(monthSel.value, minDate, maxDate);
        if (inferred) yearSel.value = inferred;
      }
      refillSelect(yearSel, years, null, yearSel.value, 'Year');

      var months = monthsInWindow(yearSel.value, minDate, maxDate);
      var monthLabels = [];
      var i;
      for (i = 0; i < months.length; i++) {
        monthLabels.push(MONTH_NAMES[months[i] - 1]);
      }
      refillSelect(monthSel, months, monthLabels, monthSel.value, 'Month');

      var bounds = dayBounds(yearSel.value, monthSel.value, minDate, maxDate);
      var days = [];
      if (bounds) {
        var d;
        for (d = bounds.start; d <= bounds.end; d++) days.push(d);
      }
      refillSelect(daySel, days, null, daySel.value, 'Day');

      syncLegacySendOnValue(wrap);
      if (sendOn) {
        sendOn.removeAttribute('aria-invalid');
        sendOn.setCustomValidity('');
      }
      if (source !== 'init') clearFieldError(wrap, 'send-on');
    }

    row.appendChild(monthSel);
    row.appendChild(daySel);
    row.appendChild(yearSel);
    if (sendOn.parentNode) {
      if (sendOn.nextSibling) sendOn.parentNode.insertBefore(row, sendOn.nextSibling);
      else sendOn.parentNode.appendChild(row);
    }

    var lab = sendOn.id ? wrap.querySelector('label[for="' + sendOn.id + '"]') : null;
    if (lab && monthSel.id) lab.setAttribute('for', monthSel.id);

    refreshParts('init');
    monthSel.addEventListener('change', function () {
      refreshParts('month');
    });
    daySel.addEventListener('change', function () {
      refreshParts('day');
    });
    yearSel.addEventListener('change', function () {
      refreshParts('year');
    });
  }

  function setRecipientPanelOpen(wrap, on) {
    if (!wrap) return;
    var fields = wrap.querySelector('[data-recipient-fields]');
    var cb = wrap.querySelector('[data-recipient-enable]');
    if (fields) {
      if (on) {
        fields.removeAttribute('hidden');
        fields.setAttribute('aria-hidden', 'false');
      } else {
        fields.setAttribute('hidden', '');
        fields.setAttribute('aria-hidden', 'true');
      }
    }
    if (cb) cb.setAttribute('aria-expanded', on ? 'true' : 'false');
  }

  function normalizeRecipientValues(wrap) {
    if (!wrap || !isEnabled(wrap)) return;
    var email = wrap.querySelector('[data-recipient-email]');
    var name = wrap.querySelector('[data-recipient-name]');
    var message = wrap.querySelector('[data-recipient-message]');
    var sendOn = wrap.querySelector('[data-recipient-send-on]');
    if (email) email.value = email.value.trim();
    if (name) name.value = name.value.trim();
    if (message) message.value = message.value.trim();
    if (sendOn) sendOn.value = sendOn.value.trim();
  }

  function bindFieldErrorClear(wrap, fieldKey, inputSelector) {
    var input = wrap.querySelector(inputSelector);
    if (!input) return;
    input.addEventListener('input', function () {
      input.removeAttribute('aria-invalid');
      input.setCustomValidity('');
      clearFieldError(wrap, fieldKey);
      if (!wrap.querySelector('[data-recipient-field-error]:not([hidden])')) {
        wrap.classList.remove('gift-card-recipient--error');
      }
    });
  }

  function validate(wrap) {
    if (!wrap || !isEnabled(wrap)) return true;

    var email = wrap.querySelector('[data-recipient-email]');
    var name = wrap.querySelector('[data-recipient-name]');
    var message = wrap.querySelector('[data-recipient-message]');
    var sendOn = wrap.querySelector('[data-recipient-send-on]');

    var emailCheck = validateRecipientEmail(email);
    if (!emailCheck.ok) {
      if (email) email.setCustomValidity(emailCheck.message);
      return showFieldError(wrap, 'email', emailCheck.message, email);
    }

    if (name && name.value.length > MAX_NAME) {
      return showFieldError(wrap, 'name', 'Recipient name cannot be longer than 255 characters.', name);
    }

    if (message && message.value.length > MAX_MESSAGE) {
      return showFieldError(wrap, 'message', 'Message cannot be longer than 200 characters.', message);
    }

    if (legacySendOnPartial(wrap)) {
      return showFieldError(
        wrap,
        'send-on',
        'Please enter a valid send date.',
        legacySendOnFirstEmpty(wrap)
      );
    }

    if (sendOn && sendOn.value.trim()) {
      var chosen = parseSendOn(sendOn.value);
      if (!chosen) {
        return showFieldError(wrap, 'send-on', 'Please enter a valid send date.', sendOn);
      }
      var today = startOfDay(new Date());
      var max = new Date(today);
      max.setDate(max.getDate() + MAX_SCHEDULE_DAYS);
      var chosenDay = startOfDay(chosen);
      if (chosenDay < today) {
        return showFieldError(wrap, 'send-on', 'Send date cannot be in the past.', sendOn);
      }
      if (chosenDay > max) {
        return showFieldError(
          wrap,
          'send-on',
          'Send date can be scheduled up to 90 days in the future.',
          sendOn
        );
      }
    }

    var offset = wrap.querySelector('[data-recipient-offset]');
    if (offset) offset.value = String(new Date().getTimezoneOffset());

    normalizeRecipientValues(wrap);
    clearFieldErrors(wrap);
    return true;
  }

  function bind(wrap) {
    if (!wrap || wrap.getAttribute('data-recipient-bound') === 'true') return;
    wrap.setAttribute('data-recipient-bound', 'true');

    var cb = wrap.querySelector('[data-recipient-enable]');
    if (cb) {
      if (typeof window.motSyncAddonToggleCheckedLabel === 'function') {
        window.motSyncAddonToggleCheckedLabel(cb);
      }
      /* motDispatchChange uses jQuery.trigger('change'), which addEventListener
         does not hear. Bind jQuery only so native change does not run this twice
         (double open + scroll was bouncing iOS tab order email→name→email). */
      function onEnableChange() {
        setFieldsEnabled(wrap, cb.checked);
        if (typeof window.motSyncAddonToggleCheckedLabel === 'function') {
          window.motSyncAddonToggleCheckedLabel(cb);
        }
      }
      if (window.jQuery) {
        window.jQuery(cb).on('change.motGiftCardRecipient', onEnableChange);
      } else {
        cb.addEventListener('change', onEnableChange);
      }
    }

    var sendOn = wrap.querySelector('[data-recipient-send-on]');
    if (sendOn) {
      applySendOnWindow(sendOn);
      bindLegacySendOn(wrap, sendOn);
      sendOn.addEventListener('focus', function () {
        applySendOnWindow(sendOn);
      });
      sendOn.addEventListener('change', function () {
        applySendOnWindow(sendOn);
        clampSendOnToWindow(sendOn);
      });
      sendOn.addEventListener('blur', function () {
        clampSendOnToWindow(sendOn);
      });
    }

    setRecipientPanelOpen(wrap, isEnabled(wrap));

    bindFieldErrorClear(wrap, 'email', '[data-recipient-email]');
    bindFieldErrorClear(wrap, 'name', '[data-recipient-name]');
    bindFieldErrorClear(wrap, 'message', '[data-recipient-message]');
    bindFieldErrorClear(wrap, 'send-on', '[data-recipient-send-on]');

    var message = wrap.querySelector('[data-recipient-message]');
    if (message) {
      syncMessageCount(wrap);
      message.addEventListener('input', function () {
        syncMessageCount(wrap);
      });
    }
  }

  function init() {
    var wrap = root();
    if (wrap) bind(wrap);
  }

  window.motValidateGiftCardRecipient = function () {
    var wrap = root();
    if (!wrap) return true;
    return validate(wrap);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
